import { Component } from '@theme/component';

/**
 * BTU calculator, shared by the calculator page (sections/cr-btu-calculator-page.liquid)
 * and the product page's BTU dialog (blocks/cr-btu-helper.liquid).
 *
 * Formula, documented on the calculator page from the same factor values:
 *   BTU = room volume (m³) × BTU per m³ for the room type
 *         × insulation × window glazing × window area × external walls × target temperature
 *   Watts = BTU ÷ 3.412
 * The recommended range is −10% to +10% of the result.
 *
 * Only `form`, `value`, `min` and `max` are required. The page adds optional refs for
 * watts, progress, saved rooms, print, email and recommendations.
 *
 * @extends {Component<{
 *   form: HTMLFormElement, value: HTMLElement, min: HTMLElement, max: HTMLElement,
 *   watts?: HTMLElement, result?: HTMLElement, status?: HTMLElement,
 *   saveButton?: HTMLButtonElement, rooms?: HTMLElement, roomsEmpty?: HTMLElement,
 *   totalBtu?: HTMLElement, totalWatts?: HTMLElement, roomsTotal?: HTMLElement,
 *   emailLink?: HTMLAnchorElement, emailBody?: HTMLTextAreaElement, emailForm?: HTMLFormElement,
 *   recommendations?: HTMLElement, recommendationsNote?: HTMLElement, recommendationsCard?: HTMLElement
 * }>}
 */
class CrBtuCalculator extends Component {
  requiredRefs = ['form', 'value', 'min', 'max'];

  static FEET_PER_METRE = 3.28084;
  static WATTS_PER_BTU = 1 / 3.412;
  static STORAGE_KEY = 'cr-btu-rooms';

  /** @type {number | undefined} */
  #timer;

  /** @type {{ btu: number, watts: number, valid: boolean, summary: string } | null} */
  #current = null;

  /** @type {Array<{ name: string, btu: number, watts: number, detail: string }>} */
  #rooms = [];

  /** Step 2 counts as done once the visitor has looked at the room details. */
  #detailsTouched = false;

  /** @type {Promise<Array<{ t: string, u: string, i?: string, p?: string, o: Array<[string, number]> }>> | null} */
  #radiators = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener('input', this.#onInput);
    this.refs.form.addEventListener('change', this.#onChange);
    this.refs.form.addEventListener('submit', (event) => event.preventDefault());
    this.refs.emailForm?.addEventListener('submit', this.#onEmailSubmit);
    this.#rooms = this.#loadRooms();
    this.#renderRooms();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener('input', this.#onInput);
    this.refs.form.removeEventListener('change', this.#onChange);
    this.refs.emailForm?.removeEventListener('submit', this.#onEmailSubmit);
  }

  get #format() {
    return new Intl.NumberFormat(document.documentElement.lang || undefined);
  }

  // Debounced so screen readers announce the settled value, not every keystroke.
  #onInput = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#update(true), 350);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'units') {
      this.#switchUnits(target.value);
    }
    if (target instanceof HTMLElement && target.closest('[data-step="2"]')) this.#detailsTouched = true;
    this.#update(true);
  };

  /** @param {string} units */
  #switchUnits(units) {
    const toFeet = units === 'ft';
    this.refs.form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const minMetres = Number(input.dataset.minM);
      const maxMetres = Number(input.dataset.maxM);
      const factor = toFeet ? CrBtuCalculator.FEET_PER_METRE : 1;
      input.min = String(Math.round(minMetres * factor * 10) / 10);
      input.max = String(Math.round(maxMetres * factor * 10) / 10);
      const value = parseFloat(input.value);
      if (Number.isFinite(value)) {
        const converted = toFeet ? value * CrBtuCalculator.FEET_PER_METRE : value / CrBtuCalculator.FEET_PER_METRE;
        input.value = String(Math.round(converted * 10) / 10);
      }
    });
    this.querySelectorAll('[data-unit-label]').forEach((label) => {
      label.textContent = toFeet ? 'ft' : 'm';
    });
  }

  /**
   * Validates one measurement, shows or clears its message, and returns metres.
   * @param {string} name
   * @param {string} label
   * @returns {number | null}
   */
  #measurement(name, label) {
    const input = this.refs.form.elements.namedItem(name);
    if (!(input instanceof HTMLInputElement)) return null;
    const message = this.refs.form.querySelector(`[data-error-for="${name}"]`);
    const unit = this.#units === 'ft' ? 'ft' : 'm';
    const value = parseFloat(input.value);
    const min = Number(input.min);
    const max = Number(input.max);
    const valid = Number.isFinite(value) && value >= min && value <= max;

    input.setAttribute('aria-invalid', String(!valid));
    if (message instanceof HTMLElement) {
      message.hidden = valid;
      message.textContent = valid ? '' : `Enter a ${label} between ${min} and ${max} ${unit}.`;
    }
    if (!valid) return null;
    return this.#units === 'ft' ? value / CrBtuCalculator.FEET_PER_METRE : value;
  }

  get #units() {
    const checked = this.refs.form.querySelector('input[name="units"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : 'm';
  }

  /** @param {string} name @param {number} fallback */
  #factor(name, fallback) {
    const field = this.refs.form.elements.namedItem(name);
    const value = field instanceof HTMLSelectElement ? parseFloat(field.value) : NaN;
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /** @param {string} name */
  #optionText(name) {
    const field = this.refs.form.elements.namedItem(name);
    return field instanceof HTMLSelectElement ? (field.selectedOptions[0]?.textContent ?? '').trim() : '';
  }

  /** @param {boolean} [track] */
  #update(track = false) {
    const width = this.#measurement('width', 'width');
    const length = this.#measurement('length', 'length');
    const height = this.#measurement('height', 'ceiling height');
    const valid = width !== null && length !== null && height !== null;

    if (!valid) {
      this.#current = { btu: 0, watts: 0, valid: false, summary: '' };
      this.refs.value.textContent = '–';
      this.refs.min.textContent = '–';
      this.refs.max.textContent = '–';
      if (this.refs.watts) this.refs.watts.textContent = '–';
      this.#renderProgress(false);
      this.#renderShare();
      if (this.refs.saveButton) this.refs.saveButton.disabled = true;
      return;
    }

    const volume = width * length * height;
    const btu = Math.round(
      volume *
        this.#factor('room', 140) *
        this.#factor('insulation', 1.25) *
        this.#factor('windows', 1.25) *
        this.#factor('glazing', 1) *
        this.#factor('walls', 1) *
        this.#factor('target', 1)
    );
    const watts = Math.round(btu * CrBtuCalculator.WATTS_PER_BTU);
    const format = this.#format;
    const previous = this.#current?.btu;

    this.refs.value.textContent = format.format(btu);
    this.refs.min.textContent = format.format(Math.round(btu * 0.9));
    this.refs.max.textContent = format.format(Math.round(btu * 1.1));
    if (this.refs.watts) this.refs.watts.textContent = format.format(watts);
    this.dataset.btu = String(btu);

    const unit = this.#units === 'ft' ? 'ft' : 'm';
    const input = (/** @type {string} */ name) => {
      const field = this.refs.form.elements.namedItem(name);
      return field instanceof HTMLInputElement ? field.value : '';
    };
    const detail = `${this.#optionText('room')}, ${input('width')} × ${input('length')} × ${input('height')} ${unit}`;
    this.#current = { btu, watts, valid: true, summary: detail };

    if (previous !== undefined && previous !== btu && this.refs.result) this.#highlight(this.refs.result);
    if (this.refs.saveButton) this.refs.saveButton.disabled = false;

    this.#renderProgress(true);
    this.#renderShare();
    this.#recommend(btu);

    if (track) {
      document.dispatchEvent(new CustomEvent('cr:track', { detail: { event: 'btu_calculated', btu } }));
    }
  }

  /** @param {HTMLElement} element */
  #highlight(element) {
    element.classList.remove('cr-value-changed');
    void element.offsetWidth;
    element.classList.add('cr-value-changed');
    element.addEventListener('animationend', () => element.classList.remove('cr-value-changed'), { once: true });
  }

  /** @param {boolean} valid */
  #renderProgress(valid) {
    const steps = Array.from(this.querySelectorAll('[data-progress-step]'));
    if (steps.length === 0) return;
    const done = [valid, valid && this.#detailsTouched, valid, this.#rooms.length > 0];
    let current = done.findIndex((isDone) => !isDone);
    if (current === -1) current = steps.length - 1;
    steps.forEach((step, index) => {
      step.classList.toggle('is-complete', Boolean(done[index]));
      if (index === current) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
    const bar = this.querySelector('[data-progress-bar]');
    if (bar instanceof HTMLElement) {
      const count = done.filter(Boolean).length;
      bar.style.setProperty('--progress', String(count / steps.length));
      bar.setAttribute('aria-valuenow', String(count));
    }
  }

  // Saved rooms --------------------------------------------------------------

  save() {
    if (!this.#current?.valid) return;
    const nameField = this.refs.form.elements.namedItem('name');
    const typed = nameField instanceof HTMLInputElement ? nameField.value.trim() : '';
    const name = typed || `${this.#optionText('room')} ${this.#rooms.length + 1}`;
    this.#rooms.push({ name, btu: this.#current.btu, watts: this.#current.watts, detail: this.#current.summary });
    this.#storeRooms();
    this.#renderRooms();
    if (nameField instanceof HTMLInputElement) nameField.value = '';
    this.#announce(`${name} saved: ${this.#format.format(this.#current.btu)} BTU.`);
    document.dispatchEvent(new CustomEvent('cr:track', { detail: { event: 'btu_room_saved', btu: this.#current.btu } }));
  }

  /** @param {Event} event */
  removeRoom(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-room-index]') : null;
    if (!(button instanceof HTMLElement)) return;
    const index = Number(button.dataset.roomIndex);
    const [removed] = this.#rooms.splice(index, 1);
    this.#storeRooms();
    this.#renderRooms();
    if (removed) this.#announce(`${removed.name} removed.`);
  }

  print() {
    document.dispatchEvent(new CustomEvent('cr:track', { detail: { event: 'btu_print' } }));
    window.print();
  }

  /** @param {string} text */
  #announce(text) {
    if (!this.refs.status) return;
    this.refs.status.textContent = text;
    this.#highlight(this.refs.status);
  }

  #loadRooms() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(CrBtuCalculator.STORAGE_KEY) ?? '[]');
      return Array.isArray(stored) ? stored.filter((room) => room && Number.isFinite(room.btu)) : [];
    } catch {
      return [];
    }
  }

  #storeRooms() {
    try {
      window.localStorage.setItem(CrBtuCalculator.STORAGE_KEY, JSON.stringify(this.#rooms));
    } catch {
      // Private browsing or blocked storage: rooms still work for this visit.
    }
  }

  #renderRooms() {
    const { rooms, roomsEmpty, totalBtu, totalWatts, roomsTotal } = this.refs;
    if (!rooms) return;
    const format = this.#format;

    rooms.replaceChildren(
      ...this.#rooms.map((room, index) => {
        const item = document.createElement('li');
        item.className = 'cr-btu-calc__room';
        const text = document.createElement('div');
        const title = document.createElement('p');
        title.className = 'cr-btu-calc__room-name';
        title.textContent = room.name;
        const meta = document.createElement('p');
        meta.className = 'cr-btu-calc__room-meta';
        meta.textContent = `${format.format(room.btu)} BTU · ${format.format(room.watts)} W · ${room.detail}`;
        text.append(title, meta);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cr-btu-calc__room-remove';
        button.dataset.roomIndex = String(index);
        button.setAttribute('on:click', '/removeRoom');
        button.setAttribute('aria-label', `Remove ${room.name}`);
        button.textContent = 'Remove';
        item.append(text, button);
        return item;
      })
    );

    const sumBtu = this.#rooms.reduce((sum, room) => sum + room.btu, 0);
    const sumWatts = this.#rooms.reduce((sum, room) => sum + room.watts, 0);
    if (totalBtu) totalBtu.textContent = format.format(sumBtu);
    if (totalWatts) totalWatts.textContent = format.format(sumWatts);
    if (roomsTotal) roomsTotal.hidden = this.#rooms.length === 0;
    if (roomsEmpty) roomsEmpty.hidden = this.#rooms.length > 0;
    this.#renderProgress(Boolean(this.#current?.valid));
    this.#renderShare();
  }

  // Sharing --------------------------------------------------------------------

  #summaryText() {
    const format = this.#format;
    const lines = ['BTU calculator results', ''];
    if (this.#rooms.length > 0) {
      for (const room of this.#rooms) {
        lines.push(`${room.name}: ${format.format(room.btu)} BTU (${format.format(room.watts)} W), ${room.detail}`);
      }
      const sumBtu = this.#rooms.reduce((sum, room) => sum + room.btu, 0);
      const sumWatts = this.#rooms.reduce((sum, room) => sum + room.watts, 0);
      lines.push('', `Total: ${format.format(sumBtu)} BTU (${format.format(sumWatts)} W)`);
    } else if (this.#current?.valid) {
      lines.push(`${this.#current.summary}: ${format.format(this.#current.btu)} BTU (${format.format(this.#current.watts)} W)`);
    }
    lines.push('', `Calculated at ${window.location.origin}${window.location.pathname}`);
    return lines.join('\n');
  }

  #renderShare() {
    const text = this.#summaryText();
    if (this.refs.emailLink) {
      const subject = encodeURIComponent(this.refs.emailLink.dataset.subject ?? 'My BTU calculator results');
      this.refs.emailLink.href = `mailto:?subject=${subject}&body=${encodeURIComponent(text)}`;
    }
    if (this.refs.emailBody) this.refs.emailBody.value = text;
  }

  #onEmailSubmit = () => {
    if (this.refs.emailBody) this.refs.emailBody.value = this.#summaryText();
    document.dispatchEvent(new CustomEvent('cr:track', { detail: { event: 'btu_results_emailed' } }));
  };

  // Recommendations ----------------------------------------------------------

  /** @param {number} btu */
  async #recommend(btu) {
    const { recommendations, recommendationsNote } = this.refs;
    const url = this.dataset.recommendUrl;
    if (!recommendations || !url) return;

    recommendations.setAttribute('aria-busy', 'true');
    this.#radiators ??= fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('script[data-cr-btu-data]');
        return JSON.parse(script?.textContent ?? '[]');
      })
      .catch(() => []);

    const radiators = await this.#radiators;
    if (Number(this.dataset.btu) !== btu) return; // A newer calculation superseded this one.

    // No radiator has per-size output data yet: hide the card rather than suggest nothing.
    if (this.refs.recommendationsCard) this.refs.recommendationsCard.hidden = radiators.length === 0;
    if (radiators.length === 0) {
      recommendations.removeAttribute('aria-busy');
      return;
    }

    // One radiator where a single size covers the room; otherwise suggest two, each at half.
    const pick = (/** @type {number} */ target) =>
      radiators
        .map((radiator) => {
          const fits = radiator.o.filter(([, output]) => output >= target * 0.9 && output <= target * 1.25);
          if (fits.length === 0) return null;
          const best = fits.reduce((a, b) => (Math.abs(b[1] - target) < Math.abs(a[1] - target) ? b : a));
          return { radiator, label: best[0], output: best[1] };
        })
        .filter((match) => match !== null)
        .sort((a, b) => Math.abs(a.output - target) - Math.abs(b.output - target))
        .slice(0, 6);

    let matches = pick(btu);
    let perRadiator = btu;
    if (matches.length === 0 && radiators.length > 0) {
      perRadiator = Math.round(btu / 2);
      matches = pick(perRadiator);
    }

    const format = this.#format;
    if (recommendationsNote) {
      recommendationsNote.textContent =
        matches.length === 0
          ? 'No radiator in our range matches this output yet. Call us and we will help you choose.'
          : perRadiator === btu
            ? `Radiators giving close to ${format.format(btu)} BTU at ΔT50:`
            : `No single radiator reaches ${format.format(btu)} BTU, so large rooms usually take two. Radiators giving about ${format.format(perRadiator)} BTU each:`;
    }

    recommendations.replaceChildren(
      ...matches.map(({ radiator, label, output }) => {
        const item = document.createElement('li');
        item.className = 'cr-btu-calc__rec';
        const link = document.createElement('a');
        link.href = radiator.u;
        link.className = 'cr-btu-calc__rec-link';
        link.dataset.crEvent = 'btu_recommendation_click';
        if (radiator.i) {
          const image = document.createElement('img');
          image.src = radiator.i;
          image.alt = '';
          image.width = 120;
          image.height = 120;
          image.loading = 'lazy';
          image.className = 'cr-btu-calc__rec-image';
          link.append(image);
        }
        const body = document.createElement('span');
        body.className = 'cr-btu-calc__rec-body';
        const title = document.createElement('span');
        title.className = 'cr-btu-calc__rec-title';
        title.textContent = radiator.t;
        const meta = document.createElement('span');
        meta.className = 'cr-btu-calc__rec-meta';
        meta.textContent = `${label} · ${format.format(output)} BTU`;
        body.append(title, meta);
        link.append(body);
        item.append(link);
        return item;
      })
    );
    recommendations.removeAttribute('aria-busy');
  }
}

if (!customElements.get('cr-btu-calculator')) customElements.define('cr-btu-calculator', CrBtuCalculator);
